import { Component, EventEmitter, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ViewportScroller } from '@angular/common';
import Utils from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import UIDefaultTheme from 'src/themes';

@Component({
  selector: 'app-signup-unified-person',
  templateUrl: './signup-unified-person.component.html',
  styleUrls: ['./signup-unified-person.component.scss'],
})
export class SignupUnifiedPersonComponent implements OnInit {
  steps: Step[] = [
    {
      icon: 'fas fa-file-alt',
      svg: 'assets/images/icon-inputdata-active.svg',
      title: 'Dados complementares',
      slug: 'complementary',
      substeps: [
        {
          title: 'Geral',
          slug: 'complementaryGeneral',
        },
        {
          title: 'Endereço',
          slug: 'complementaryAddress',
        },
        {
          title: 'Dados bancários',
          slug: 'complementaryBank',
        },
        {
          title: 'Profissional',
          slug: 'complementaryProfessional',
        },
        {
          title: 'Fundos de interesse',
          slug: 'complementaryBond',
        },
        {
          title: 'Informações de risco',
          slug: 'complementaryRisk',
        },
        {
          title: 'Representantes',
          slug: 'complementaryRepresentatives',
        },
      ],
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/icon-attachments.svg',
      title: 'Anexos & comprovantes',
      slug: 'attachments',
    },
  ];

  shareholderSteps: Step[] = [
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Rendimentos & patrimônios',
      slug: 'income',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Declarações & autorizações',
      slug: 'declarations',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Análise de perfil de investidor',
      slug: 'analysis',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Declaração de Condição de Investidor',
      slug: 'conditions',
    },
    {
      icon: 'fas fa-paperclip',
      svg: 'assets/images/check-list.svg',
      title: 'Declaração US Person / Non US Person (FATCA)',
      slug: 'tax',
    },
  ];

  _activeStep: number = 0;

  activeSubstep: PersonSteps = 'complementaryRepresentatives';

  redirected = false;
  hasMultipleAccounts = false;
  userOptionsOpen: boolean = false;

  get activeStep() {
    return this._activeStep;
  }

  set activeStep(value) {
    if (this._activeStep !== value) {
      // Scroll to top when step changes
      this.viewportScroller.scrollToPosition([0, 0]);
    }
    this._activeStep = value;

    if (this.isShareholder) {
      switch (value) {
        case 0:
          this.currentTab = 'Dados complementares';
          break;

        case 1:
          // Se não tem representantes, step 1 = income
          // Se tem representantes, step 1 = powers
          this.currentTab = !this.hasRepresentatives
            ? 'Rendimentos & patrimônios'
            : 'Resumo dos poderes';
          break;

        case 2:
          // Se não tem representantes, step 2 = declarations
          // Se tem representantes, step 2 = income
          this.currentTab = !this.hasRepresentatives
            ? 'Declarações & autorizações'
            : 'Rendimentos & patrimônios';
          break;

        case 3:
          // Se não tem representantes, step 3 = analysis
          // Se tem representantes, step 3 = declarations
          this.currentTab = !this.hasRepresentatives
            ? 'Análise de perfil de investidor'
            : 'Declarações & autorizações';
          break;

        case 4:
          // Se não tem representantes, step 4 = conditions
          // Se tem representantes, step 4 = analysis
          this.currentTab = !this.hasRepresentatives
            ? 'Declaração de Condição de Investidor'
            : 'Análise de perfil de investidor';
          break;

        case 5:
          // Se não tem representantes, step 5 = tax
          // Se tem representantes, step 5 = conditions
          this.currentTab = !this.hasRepresentatives
            ? 'Declaração US Person / Non US Person (FATCA)'
            : 'Declaração de Condição de Investidor';
          break;

        case 6:
          // Se não tem representantes, step 6 = attachments (último)
          // Se tem representantes, step 6 = tax
          this.currentTab = !this.hasRepresentatives
            ? 'Anexos & comprovantes'
            : 'Declaração US Person / Non US Person (FATCA)';
          break;

        case 7:
          // Se tem representantes, step 7 = attachments (último)
          this.currentTab = 'Anexos & comprovantes';
          break;
        default:
          this.currentTab = '';
          break;
      }
    } else {
      switch (value) {
        case 0:
          this.currentTab = 'Dados complementares';
          break;

        case 1:
          // Se não tem representantes, step 1 = attachments
          // Se tem representantes, step 1 = powers
          this.currentTab = !this.hasRepresentatives
            ? 'Anexos & comprovantes'
            : 'Resumo dos poderes';
          break;

        case 2:
          // Se tem representantes, step 2 = attachments
          this.currentTab = 'Anexos & comprovantes';
          break;
        default:
          this.currentTab = '';
          break;
      }
    }
  }

  currentTab: string = '';

  isShareholder = false;

  loading = false;

  showSupportModal = false;

  name: string | null = null;
  role: string | null = null;
  label: string | null = null;

  agentOptions: SelectItem[] = [];

  userCredentials: UserCredentials = {
    email: '',
    name: '',
  };

  isAssignor = false;

  accounts: RegisterUserResponse[] = [];

  hasRepresentatives = false;
  routeId: string = null;
  sendingRepresentativeEmails = false;
  isLocalhost =
    typeof window !== 'undefined' &&
    window.location.href.includes('localhost:4200');

  public UIDefaultTheme = UIDefaultTheme;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public signupService: SignupService,
    private apiService: ApiService,
    private toastService: ToastService,
    private authService: AuthService,
    private titleService: Title,
    private storageService: StorageService,
    private viewportScroller: ViewportScroller,
  ) {
    if (this.authService.user.active_register.register.role.slug) {
      this.titleService.setTitle(
        Utils.setTitle(
          this.authService.user.active_register.register.role.slug,
        ),
      );
    }
  }

  ngOnInit(): void {
    // Comparar o UUID da URL com o id do active_register.register
    this.routeId = this.route.snapshot.paramMap.get('id');
    const currentRegisterId =
      this.authService.user?.active_register?.register?.id;

    if (
      this.routeId &&
      currentRegisterId &&
      this.routeId !== currentRegisterId
    ) {
      this.hasMultipleAccounts = true;
    } else {
      this.hasMultipleAccounts = false;
    }

    this.isShareholder = this.router.url.includes('shareholder');
    this.isAssignor = this.router.url.includes('assignor');
    const isProvider = this.router.url.includes('provider');

    // Remover etapa de risco para cedentes e prestadores de serviço
    if (this.isAssignor || isProvider) {
      const complementaryStep = this.steps.find(
        (step) => step.slug === 'complementary',
      );
      if (complementaryStep && complementaryStep.substeps) {
        const riskIndex = complementaryStep.substeps.findIndex(
          (substep) => substep.slug === 'complementaryRisk',
        );
        if (riskIndex !== -1) {
          complementaryStep.substeps.splice(riskIndex, 1);
        }
      }
    }

    if (this.isShareholder) {
      // Para shareholders, attachments deve ser o último step
      // Remove attachments da posição inicial
      const attachmentsStep = this.steps.find(
        (step) => step.slug === 'attachments',
      );
      if (attachmentsStep) {
        const attachmentsIndex = this.steps.findIndex(
          (step) => step.slug === 'attachments',
        );
        this.steps.splice(attachmentsIndex, 1);
      }

      // Insere os shareholderSteps na posição 1 (após complementary)
      this.steps.splice(1, 0, ...this.shareholderSteps);

      // Adiciona attachments no final
      if (attachmentsStep) {
        this.steps.push(attachmentsStep);
      }
    }

    const firstName = this.authService.user?.first_name || '';
    const lastName = this.authService.user?.last_name || '';
    const fullName = [firstName, lastName]
      .filter((name) => name && name.trim() !== '')
      .join(' ')
      .trim();

    this.userCredentials = {
      email: this.authService.user?.email,
      name: fullName || '',
    };

    document.onkeydown = (e) => {
      if (e.key === 'Enter') {
        this.proceed();
      }
    };

    this.signupService.changePersonStepEvent.subscribe((step) => {
      const aux = `complementary${
        step.charAt(0).toUpperCase() + step.slice(1)
      }` as PersonSteps;

      this.activeSubstep = aux;
      this.loading = false;
      this.activeStep = 0;

      // Sempre usar o register_id da URL

      this.signupService.updateUserStepPF('register', step, this.routeId);
    });

    this.signupService.changeBasicInfoEvent.subscribe((value) => {
      this.label = this.getRoleLabel(this.role);

      this.name = value.toUpperCase();
    });

    this.signupService.fetchDataEvent.emit('PF');
    let urlParts = this.router.url.split('/pf')[0].split('/');
    let range = urlParts.length;
    this.role = urlParts[range - 1];

    this.checkRepresentativesData();

    this.signupService.checkRepresentativeDataEvent.subscribe((value) => {
      this.hasRepresentatives = value;
      this.changeSteps();
    });

    this.getData();
  }

  async getData() {
    this.loading = true;
    try {
      // setInviteInfo é síncrono, pode ser chamado imediatamente
      this.setInviteInfo();

      // Chamar getAgents em paralelo com o patch/get condicional (se necessário)
      // pois são independentes entre si
      const promises: Promise<any>[] = [this.getAgents()];

      // Aguardar getAgents e patch/get (se necessário) terminarem
      await Promise.all(promises);

      // redirectUser pode ser chamado após o patch/get condicional (se necessário)
      // pois pode depender do active_register atualizado
      await this.redirectUser();
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      this.loading = false;
    }
  }

  async getAgents() {
    try {
      const res = await this.apiService.get({
        route: 'api/registration/simplified-agent/',
      });

      this.agentOptions = res.map((item: any) => {
        return {
          label: item.name,
          value: item.id,
        };
      });
    } catch (error) {}
  }

  get hasSelectedRoleId(): boolean {
    const selectedRoleId = this.storageService.get('selected_role_id', true);
    return selectedRoleId !== null && selectedRoleId !== undefined;
  }

  async redirectToDash() {
    const selectedRoleId = this.storageService.get('selected_role_id', true);

    if (selectedRoleId && this.routeId && this.role) {
      // Se está em adição de papel, redirecionar para tela de detalhes
      this.storageService.remove('selected_role_id');

      this.router.navigateByUrl(`/app/details/${this.routeId}`);
    } else {
      // Comportamento padrão: ir para dashboard
      this.storageService.remove('selected_role_id');
      this.router.navigateByUrl('/app/dashboard');
    }
  }

  toggleUserOptions() {
    this.userOptionsOpen = !this.userOptionsOpen;
  }

  getUserInitials(string) {
    if (!string || typeof string !== 'string') {
      return '';
    }

    return string
      .toUpperCase()
      .replace(/[^\w\s]/gi, '')
      .split(' ')
      .filter(
        (word: string) =>
          word && word.trim() !== '' && word.toLowerCase() !== 'null',
      )
      .slice(0, 2)
      .map((word: string) => word.charAt(0))
      .join('');
  }

  async redirectUser() {
    try {
      // Sempre buscar trace_step usando o register_id da URL
      const response = await this.apiService.get({
        route: 'api/registration/register-step/',
        params: { register_uuid: this.routeId },
        token: true,
      });

      let { data } = response;

      if (!data) {
        data = { step: 'register', substep: 'general' };
      }

      if (data.step === 'review') {
        data.step = 'register';
        data.substep = 'general';
      }

      // Se for cedente ou prestador e o backend retornar etapa de risco, redirecionar para representantes
      const isProvider = this.router.url.includes('provider');
      if ((this.isAssignor || isProvider) && data.substep === 'risk') {
        data.substep = 'representatives';
      }

      const substepSlug = `complementary${
        data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
      }` as PersonSteps;
      this.signupService.setPersonStep(substepSlug);

      this.setBasicInfo();
      this.processStepData(data);
    } catch (error) {
      console.error('Erro ao carregar trace step:', error);
    }
  }

  processStepData(data: any) {
    if (data.step === 'register') {
      if (this.isShareholder) {
        switch (data.substep) {
          case 'powers':
            this.activeStep = 1;
            break;
          case 'income':
            // Se não tem representantes, income é step 1
            // Se tem representantes, income é step 2
            this.activeStep = !this.hasRepresentatives ? 1 : 2;
            break;
          case 'declarations':
            // Se não tem representantes, declarations é step 2
            // Se tem representantes, declarations é step 3
            this.activeStep = !this.hasRepresentatives ? 2 : 3;
            break;
          case 'analysis':
            // Se não tem representantes, analysis é step 3
            // Se tem representantes, analysis é step 4
            this.activeStep = !this.hasRepresentatives ? 3 : 4;
            break;
          case 'conditions':
            // Se não tem representantes, conditions é step 4
            // Se tem representantes, conditions é step 5
            this.activeStep = !this.hasRepresentatives ? 4 : 5;
            break;
          case 'tax':
            // Se não tem representantes, tax é step 5
            // Se tem representantes, tax é step 6
            this.activeStep = !this.hasRepresentatives ? 5 : 6;
            break;
          case 'attachments':
            // Se não tem representantes, attachments é step 6 (último)
            // Se tem representantes, attachments é step 7 (último)
            this.activeStep = !this.hasRepresentatives ? 6 : 7;
            break;
          default:
            this.activeSubstep = `complementary${
              data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
            }` as PersonSteps;
            this.signupService.setPersonStep(this.activeSubstep);
            this.activeStep = 0;
            break;
        }
      } else {
        switch (data.substep) {
          case 'powers':
            this.activeStep = 1;
            break;
          case 'attachments':
            // Se não tem representantes, attachments é o step 1 (powers foi removido)
            // Se tem representantes, attachments é o step 2
            this.activeStep = this.hasRepresentatives ? 2 : 1;
            break;
          case 'risk':
            // Se for cedente ou prestador e tentar acessar etapa de risco, redirecionar para representantes
            const isProvider = this.router.url.includes('provider');
            if (this.isAssignor || isProvider) {
              this.activeSubstep = 'complementaryRepresentatives';
              this.signupService.setPersonStep('complementaryRepresentatives');
              this.activeStep = 0;
            } else {
              this.activeSubstep = `complementary${
                data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
              }` as PersonSteps;
              this.signupService.setPersonStep(this.activeSubstep);
              this.activeStep = 0;
            }
            break;
          default:
            this.activeSubstep = `complementary${
              data.substep.charAt(0).toUpperCase() + data.substep.slice(1)
            }` as PersonSteps;
            this.signupService.setPersonStep(this.activeSubstep);
            this.activeStep = 0;
            break;
        }
      }
    }

    if (data.step === 'review') {
      this.router.navigateByUrl(`/app/details/${this.role}/${this.routeId}`);
    }

    if (data.step !== 'register' && data.step !== 'review') {
      this.router.navigateByUrl(`/app/dashboard`);
    }

    this.redirected = true;
  }

  async setBasicInfo() {
    try {
      // GET usa query parameter com register_uuid
      let apiRoute: string;
      let params: any = null;

      if (this.routeId) {
        apiRoute = 'api/registration/personal/general/';
        params = { register_uuid: this.routeId };
      } else {
        // Se não tiver routeId, usar o register.id do active_register
        const registerId = this.authService.user?.active_register?.register?.id;
        if (registerId) {
          apiRoute = 'api/registration/personal/general/';
          params = { register_uuid: registerId };
        } else {
          // Removido o fallback para pf_general/me/ - não deve ser chamado na página de declarações
          this.loading = false;
          return;
        }
      }

      const { data } = await this.apiService.get<ApiResponse<Person>>({
        route: apiRoute,
        params: params,
        token: true,
      });

      if (data && data.full_name !== null && data.full_name !== '') {
        this.label = this.getRoleLabel(this.role);

        this.name = data.full_name.toUpperCase();
      }
    } catch (error) {
      console.log(error);
    }
  }

  getRoleLabel(role: string) {
    switch (role) {
      case 'assignor':
        return 'Cedente Pessoa Física';

      case 'provider':
        const user = this.authService.getUser();

        let label = '';

        if (user.active_register.register.invite) {
          const inviteData = user.active_register.register.invite.data;

          if (
            !inviteData.agent ||
            !Array.isArray(inviteData.agent) ||
            inviteData.agent.length === 0
          ) {
            return 'Prestador de Serviço';
          }

          label = inviteData.agent
            .map((item) => {
              const foundAgent = this.agentOptions.find(
                (agent) => agent.value === item,
              );
              return foundAgent ? foundAgent.label : null;
            })
            .filter((label) => label !== null)
            .join(', ');
        } else {
          if (!user.active_register.register.agent) {
            return 'Prestador de Serviço';
          }
          label = user.active_register.register.agent
            .map((item) => item.name)
            .join(', ');
        }

        return label || 'Prestador de Serviço';

      default:
        return 'Cotista Pessoa Física';
    }
  }

  async sendApproval() {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toastService.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota',
        );
        return false;
      }

      // Verificar se há um role_id no storage (papel selecionado no dropdown de edição)
      const selectedRoleId = this.storageService.get('selected_role_id', true);

      // Buscar dados atualizados do register da API para obter role_draft
      let registerResponse: any = null;
      let roleDraft = null;
      try {
        registerResponse = await this.apiService.get<any>({
          route: `api/registration/register/${routeId}`,
          token: true,
        });

        // role_draft pode ser um objeto ou um ID, pegar o ID
        const roleDraftData = registerResponse?.data?.role_draft;
        roleDraft = roleDraftData?.id || roleDraftData;
      } catch (error) {
        console.error('Erro ao buscar dados do register:', error);
        // Fallback para authService se a API falhar
        const fallbackRoleDraft =
          this.authService.user?.active_register?.register?.role_draft;
        roleDraft = fallbackRoleDraft?.id || fallbackRoleDraft;
      }

      // Priorizar role_id do storage (papel selecionado no dropdown) sobre role_draft
      const roleIdToUse = selectedRoleId || roleDraft;

      if (roleIdToUse) {
        // Se houver role_id (do storage ou role_draft), fazer as 3 chamadas na sequência:

        // 1. Primeiro PATCH: adicionar role_id
        try {
          await this.apiService.patch({
            route: `api/v3/registration/registers/`,
            body: {
              register_uuid: routeId,
              role_id: roleIdToUse,
            },
            token: true,
          });

          // Limpar o storage após usar o role_id
          if (selectedRoleId) {
            this.storageService.remove('selected_role_id');
          }
        } catch (error) {
          console.error('Erro ao adicionar role_id:', error);
          // Continua mesmo se falhar
        }

        // 2. POST send-to-approval
        await this.apiService.post({
          route: 'api/approvals/send-to-approval/',
          params: { register_uuid: routeId },
          body: {},
          token: true,
        });
        // await this.resetRepresentativesSignStatus(routeId);

        // Enviar emails para representantes após send-to-approval
        // try {
        //   await this.apiService.post({
        //     route: 'api/approvals/send-representative-emails/',
        //     params: { register_uuid: routeId },
        //     body: {},
        //     token: true,
        //   });
        // } catch (err) {
        //   console.error('Erro ao enviar emails para representantes:', err);
        // }

        // 3. Segundo PATCH: limpar role_draft
        try {
          await this.apiService.patch({
            route: `api/v3/registration/registers/`,
            body: {
              register_uuid: routeId,
              role_draft: null,
            },
            token: true,
          });
        } catch (error) {
          console.error('Erro ao limpar role_draft:', error);
          // Continua mesmo se falhar
        }
      } else {
        // Se não houver role_draft, apenas fazer send-to-approval (comportamento normal)
        await this.apiService.post({
          route: 'api/approvals/send-to-approval/',
          params: { register_uuid: routeId },
          body: {},
          token: true,
        });
        // await this.resetRepresentativesSignStatus(routeId);

        // Enviar emails para representantes após send-to-approval
        // try {
        //   await this.apiService.post({
        //     route: 'api/approvals/send-representative-emails/',
        //     params: { register_uuid: routeId },
        //     body: {},
        //     token: true,
        //   });
        // } catch (err) {
        //   console.error('Erro ao enviar emails para representantes:', err);
        // }
      }

      const regRole = registerResponse?.data?.role;
      const roleIdFromRegister = regRole?.id ?? regRole;
      const targetRoleId =
        roleIdToUse ||
        roleIdFromRegister ||
        this.authService.user?.active_register?.register?.role?.id;
      const targetApplicable =
        regRole?.applicable ||
        this.authService.user?.active_register?.register?.role?.applicable;

      let papersForPatch: any[] = registerResponse?.data?.papers ?? [];
      try {
        const freshRegister = await this.apiService.get<any>({
          route: `api/registration/register/${routeId}`,
          token: true,
        });
        if (
          Array.isArray(freshRegister?.data?.papers) &&
          freshRegister.data.papers.length > 0
        ) {
          papersForPatch = freshRegister.data.papers;
        }
      } catch (e) {
        console.error('Erro ao buscar papers para atualização de status:', e);
      }
      const authReg = this.authService.user?.active_register?.register as any;
      if (
        (!papersForPatch || papersForPatch.length === 0) &&
        Array.isArray(authReg?.papers)
      ) {
        papersForPatch = authReg.papers;
      }

      let paperId: string | null = null;
      if (Array.isArray(papersForPatch) && papersForPatch.length > 0) {
        const wantNum =
          targetRoleId != null && targetRoleId !== ''
            ? Number(targetRoleId)
            : NaN;
        if (!Number.isNaN(wantNum)) {
          const byRoleId = papersForPatch.find(
            (p: any) =>
              p?.role?.id != null && Number(p.role.id) === wantNum,
          );
          if (byRoleId?.id != null) {
            paperId = String(byRoleId.id).trim();
          }
        }
        if (!paperId && targetApplicable) {
          const ta = String(targetApplicable).toLowerCase();
          const byApp = papersForPatch.find(
            (p: any) =>
              p?.role?.applicable &&
              String(p.role.applicable).toLowerCase() === ta,
          );
          if (byApp?.id != null) {
            paperId = String(byApp.id).trim();
          }
        }
        if (!paperId && papersForPatch.length === 1) {
          const only = papersForPatch[0];
          paperId = only?.id != null ? String(only.id).trim() : null;
        }
      }

      if (paperId) {
        try {
          await this.apiService.patch({
            route: `api/v2/registration/registration-papers/${paperId}/`,
            token: true,
            body: { status: 'em aprovação' },
          });
        } catch (err) {
          console.error('Erro ao atualizar papers (status em aprovação):', err);
        }
      }

      this.toastService.show(
        'info',
        'Sucesso',
        'Solicitação de aprovação enviada com sucesso',
      );

      return true;
    } catch (error) {
      if (error.error?.sync_error) {
        this.toastService.show(
          'warning',
          'Atenção',
          `Solicitação enviada, mas: ${error.error.sync_error}`,
        );
        this.activeStep--;
        return false;
      }

      this.toastService.show(
        'error',
        'Erro',
        'Falha ao enviar solicitação de aprovação ao sistema',
      );

      this.activeStep--;

      return false;
    }
  }

  canProceed() {
    if (this.isShareholder) {
      switch (this.activeStep) {
        case 0:
          // Usar activeSubstep (aba visível no stepper), não getPersonStep — pode dessincronizar
          return !!this.signupService.getPersonFormStatus(this.activeSubstep);

        case 1:
          // Se não tem representantes, pula para os próximos steps (income)
          if (!this.hasRepresentatives) {
            // Para cotista PF, outros bens são obrigatórios
            const income =
              this.signupService.getPersonFormStatus('incomeSalary');
            const incomeOthers =
              this.signupService.getPersonFormStatus('incomeOthers');
            return income && incomeOthers;
          }
          const powers = this.signupService.getPersonFormStatus('powers');
          return powers;

        case 2:
          // Se não tem representantes, case 2 = declarations
          if (!this.hasRepresentatives) {
            const declarations =
              this.signupService.getPersonFormStatus('declarations');
            return declarations;
          }
          // Se tem representantes, case 2 = income
          // Para cotista PF, outros bens são obrigatórios
          const income = this.signupService.getPersonFormStatus('incomeSalary');
          const incomeOthers =
            this.signupService.getPersonFormStatus('incomeOthers');
          return income && incomeOthers;

        case 3:
          // Se não tem representantes, case 3 = analysis
          if (!this.hasRepresentatives) {
            const analysis = this.signupService.getPersonFormStatus('analysis');
            return analysis;
          }
          // Se tem representantes, case 3 = declarations
          const declarations =
            this.signupService.getPersonFormStatus('declarations');
          return declarations;

        case 4:
          // Se não tem representantes, case 4 = conditions
          if (!this.hasRepresentatives) {
            const conditions =
              this.signupService.getPersonFormStatus('conditions');
            return conditions;
          }
          // Se tem representantes, case 4 = analysis
          const analysis = this.signupService.getPersonFormStatus('analysis');
          return analysis;

        case 5:
          // Se não tem representantes, case 5 = tax
          if (!this.hasRepresentatives) {
            const tax = this.signupService.getPersonFormStatus('tax');
            return tax;
          }
          // Se tem representantes, case 5 = conditions
          const conditions =
            this.signupService.getPersonFormStatus('conditions');
          return conditions;

        case 6:
          // Se não tem representantes, case 6 = attachments
          if (!this.hasRepresentatives) {
            const attachment =
              this.signupService.getPersonFormStatus('attachments');
            return attachment;
          }
          // Se tem representantes, case 6 = tax
          const tax = this.signupService.getPersonFormStatus('tax');
          return tax;

        case 7:
          // Se não tem representantes, case 7 = final
          if (!this.hasRepresentatives) {
            return true;
          }
          // Se tem representantes, case 7 = attachments
          const attachment =
            this.signupService.getPersonFormStatus('attachments');
          return attachment;

        case 8:
          // Se tem representantes, case 8 = final
          return true;
        default:
          return false;
      }
    } else {
      switch (this.activeStep) {
        case 0:
          return !!this.signupService.getPersonFormStatus(this.activeSubstep);

        case 1:
          // Se não tem representantes, step 1 = attachments
          if (!this.hasRepresentatives) {
            const attachment =
              this.signupService.getPersonFormStatus('attachments');
            return attachment;
          }
          // Se tem representantes, step 1 = powers
          const powers = this.signupService.getPersonFormStatus('powers');
          return powers;

        case 2:
          // Se tem representantes, step 2 = attachments
          const attachment =
            this.signupService.getPersonFormStatus('attachments');
          return attachment;

        case 3:
          return true;
        default:
          return false;
      }
    }
  }

  getButtonIcon(stepAction: string) {
    if (stepAction === 'next') {
      return this.canProceed()
        ? 'assets/images/icon_button_arrow.svg'
        : 'assets/images/icon_button_block.svg';
    } else {
      return this.activeStep === 0
        ? 'assets/images/icon_button_block.svg'
        : 'assets/images/icon_button_arrow.svg';
    }
  }

  setActiveStep(event: any) {
    this.activeStep = event;
    // Ensure scroll to top when clicking directly on stepper
    setTimeout(() => {
      this.viewportScroller.scrollToPosition([0, 0]);
    }, 0);
  }

  runBack() {
    if (this.isShareholder) {
      switch (this.activeStep) {
        case 6:
          // Se não tem representantes, step 6 = attachments, volta para step 5 (tax)
          // Se tem representantes, step 6 = tax, volta para step 5 (conditions)
          this.activeStep--;
          if (!this.hasRepresentatives) {
            // Quando não tem representantes, step 6 = attachments, então volta para tax (step 5)
            this.signupService.updateUserStepPF(
              'register',
              'tax',
              this.routeId,
            );
          } else {
            // Quando tem representantes, step 6 = tax, então volta para conditions (step 5)
            this.signupService.updateUserStepPF(
              'register',
              'conditions',
              this.routeId,
            );
          }

          break;

        case 7:
          // Se tem representantes, step 7 = attachments, volta para step 6 (tax)
          this.activeStep--;
          this.signupService.updateUserStepPF('register', 'tax', this.routeId);
          break;

        case 5:
          // Se não tem representantes, step 5 = tax, volta para step 4 (conditions)
          // Se tem representantes, step 5 = conditions, volta para step 4 (analysis)
          this.activeStep--;

          if (!this.hasRepresentatives) {
            // Quando não tem representantes, step 5 = tax, então volta para conditions (step 4)
            this.signupService.updateUserStepPF(
              'register',
              'conditions',
              this.routeId,
            );
          } else {
            // Quando tem representantes, step 5 = conditions, então volta para analysis (step 4)
            this.signupService.updateUserStepPF(
              'register',
              'analysis',
              this.routeId,
            );
          }
          break;

        case 4:
          // Se não tem representantes, step 4 = conditions, volta para step 3 (analysis)
          // Se tem representantes, step 4 = analysis, volta para step 3 (declarations)
          this.activeStep--;
          if (!this.hasRepresentatives) {
            // Quando não tem representantes, step 4 = conditions, então volta para analysis (step 3)
            this.signupService.updateUserStepPF(
              'register',
              'analysis',
              this.routeId,
            );
          } else {
            // Quando tem representantes, step 4 = analysis, então volta para declarations (step 3)
            this.signupService.updateUserStepPF(
              'register',
              'declarations',
              this.routeId,
            );
          }
          break;

        case 3:
          // Se não tem representantes, step 3 = analysis, volta para step 2 (declarations)
          // Se tem representantes, step 3 = declarations, volta para step 2 (income)
          this.activeStep--;

          if (!this.hasRepresentatives) {
            // Quando não tem representantes, step 3 = analysis, então volta para declarations (step 2)
            this.signupService.updateUserStepPF(
              'register',
              'declarations',
              this.routeId,
            );
          } else {
            // Quando tem representantes, step 3 = declarations, então volta para income (step 2)
            this.signupService.updateUserStepPF(
              'register',
              'income',
              this.routeId,
            );
          }

          break;

        case 2:
          // Se não tem representantes, step 2 = declarations, volta para step 1 (income)
          // Se tem representantes, step 2 = income, volta para step 1 (powers)
          if (!this.hasRepresentatives) {
            this.activeStep = 1; // Vai direto para income (step 1)

            this.signupService.updateUserStepPF(
              'register',
              'income',
              this.routeId,
            );
          } else {
            this.activeStep--;

            this.signupService.updateUserStepPF(
              'register',
              'income',
              this.routeId,
            );
          }
          break;

        case 1:
          // Se não tem representantes, step 1 = income, volta para step 0 (representatives)
          // Se tem representantes, step 1 = powers, volta para step 0 (representatives)
          this.activeStep--;

          this.signupService.changePersonStepEvent.emit('representatives');

          this.signupService.setPersonStep('complementaryRepresentatives');
          break;

        case 0:
          switch (this.activeSubstep) {
            case 'complementaryAddress':
              this.signupService.changePersonStepEvent.emit('general');

              this.signupService.setPersonStep('complementaryGeneral');
              break;

            case 'complementaryBank':
              this.signupService.changePersonStepEvent.emit('address');

              this.signupService.setPersonStep('complementaryAddress');
              break;

            case 'complementaryProfessional':
              this.signupService.changePersonStepEvent.emit('bank');

              this.signupService.setPersonStep('complementaryBank');
              break;

            case 'complementaryBond':
              this.signupService.changePersonStepEvent.emit('professional');

              this.signupService.setPersonStep('complementaryProfessional');
              break;

            case 'complementaryRisk':
              // Volta para Fundos de interesse
              this.signupService.changePersonStepEvent.emit('bond');
              this.signupService.setPersonStep('complementaryBond');
              break;

            case 'complementaryRepresentatives':
              // Se for cedente ou prestador, pula a etapa de risco e volta para bond
              const isProviderRep = this.router.url.includes('provider');
              if (this.isAssignor || isProviderRep) {
                this.signupService.changePersonStepEvent.emit('bond');
                this.signupService.setPersonStep('complementaryBond');
              } else {
                this.signupService.changePersonStepEvent.emit('risk');
                this.signupService.setPersonStep('complementaryRisk');
              }
              break;

            default:
              break;
          }

          break;
        default:
          break;
      }
    } else {
      switch (this.activeStep) {
        case 2:
          this.activeStep--;

          if (this.hasRepresentatives && this.isAssignor) {
            this.signupService.updateUserStepPF(
              'register',
              'powers',
              this.routeId,
            );
          } else {
            this.signupService.setPersonStep('complementaryRepresentatives');
            this.signupService.changePersonStepEvent.emit('representatives');
          }
          break;

        case 1:
          // Se não tem representantes, case 1 = attachments, então volta para step 0
          // Se tem representantes, case 1 = powers, então volta para step 0 também
          this.activeStep--;

          this.signupService.changePersonStepEvent.emit('representatives');

          this.signupService.setPersonStep('complementaryRepresentatives');
          break;

        case 0:
          switch (this.activeSubstep) {
            case 'complementaryAddress':
              this.signupService.changePersonStepEvent.emit('general');

              this.signupService.setPersonStep('complementaryGeneral');
              break;

            case 'complementaryBank':
              this.signupService.changePersonStepEvent.emit('address');

              this.signupService.setPersonStep('complementaryAddress');
              break;

            case 'complementaryProfessional':
              this.signupService.changePersonStepEvent.emit('bank');

              this.signupService.setPersonStep('complementaryBank');
              break;

            case 'complementaryBond':
              this.signupService.changePersonStepEvent.emit('professional');

              this.signupService.setPersonStep('complementaryProfessional');
              break;

            case 'complementaryRisk':
              // Volta para Fundos de interesse
              this.signupService.changePersonStepEvent.emit('bond');
              this.signupService.setPersonStep('complementaryBond');
              break;

            case 'complementaryRepresentatives':
              // Se for cedente ou prestador, pula a etapa de risco e volta para bond
              const isProviderRep = this.router.url.includes('provider');
              if (this.isAssignor || isProviderRep) {
                this.signupService.changePersonStepEvent.emit('bond');
                this.signupService.setPersonStep('complementaryBond');
              } else {
                this.signupService.changePersonStepEvent.emit('risk');
                this.signupService.setPersonStep('complementaryRisk');
              }
              break;

            default:
              break;
          }

          break;
        default:
          break;
      }
    }
  }

  async proceed() {
    this.loading = true;

    const routeId = this.route.snapshot.paramMap.get('id');
    if (this.canProceed()) {
      if (this.isShareholder) {
        switch (this.activeStep) {
          case 0:
            let step = this.signupService.getPersonStep();

            if (step !== 'complementaryRepresentatives') {
              this.loading = false;
              this.signupService.sendPersonData.emit(step);
              return;
            }

            if (this.hasRepresentatives) {
              await this.signupService.updateUserStepPF(
                'register',
                'powers',
                this.routeId,
              );

              this.activeStep++;
            } else {
              // Se não tem representantes, vai direto para income (step 1)
              // Não precisa pular o step de powers porque ele não existe no array

              await this.signupService.updateUserStepPF(
                'register',
                'income',
                this.routeId,
              );

              this.activeStep = 1; // income é o step 1 quando não há representantes
            }

            break;

          case 1:
            if (!this.hasRepresentatives) {
              this.signupService.sendPersonData.emit('income');

              await this.signupService.updateUserStepPF(
                'register',
                'declarations',
                this.routeId,
              );
              this.activeStep++;
            } else {
              this.signupService.sendPersonData.emit('powers');

              await this.signupService.updateUserStepPF(
                'register',
                'income',
                this.routeId,
              );
              this.activeStep++;
            }

            break;

          case 2:
            if (!this.hasRepresentatives) {
              this.signupService.sendPersonData.emit('declarations');

              await this.signupService.updateUserStepPF(
                'register',
                'analysis',
                this.routeId,
              );
              this.activeStep++;
            } else {
              this.signupService.sendPersonData.emit('income');

              await this.signupService.updateUserStepPF(
                'register',
                'declarations',
                this.routeId,
              );
              this.activeStep++;
            }

            break;

          case 3:
            if (!this.hasRepresentatives) {
              this.signupService.sendPersonData.emit('analysis');

              await this.signupService.updateUserStepPF(
                'register',
                'conditions',
                this.routeId,
              );

              this.activeStep++;
            } else {
              this.signupService.sendPersonData.emit('declarations');

              await this.signupService.updateUserStepPF(
                'register',
                'analysis',
                this.routeId,
              );

              this.activeStep++;
            }

            break;

          case 4:
            if (!this.hasRepresentatives) {
              this.signupService.sendPersonData.emit('conditions');

              await this.signupService.updateUserStepPF(
                'register',
                'tax',
                this.routeId,
              );

              this.activeStep++;
            } else {
              this.signupService.sendPersonData.emit('analysis');

              await this.signupService.updateUserStepPF(
                'register',
                'conditions',
                this.routeId,
              );
              this.activeStep++;
            }

            break;

          case 5:
            if (!this.hasRepresentatives) {
              this.signupService.sendPersonData.emit('tax');

              await this.signupService.updateUserStepPF(
                'register',
                'attachments',
                this.routeId,
              );
              this.activeStep++;
            } else {
              this.signupService.sendPersonData.emit('conditions');
              await this.signupService.updateUserStepPF(
                'register',
                'tax',
                this.routeId,
              );
              this.activeStep++;
            }

            break;

          case 6:
            if (!this.hasRepresentatives) {
              this.activeStep++;

              const approvalDone = await this.sendApproval();
              if (approvalDone) {
                await this.signupService.updateUserStepPF(
                  'review',
                  'general',
                  this.routeId,
                );

                this.router.navigateByUrl(
                  `/app/details/${this.role}/${this.routeId}`,
                );
              }
            } else {
              this.signupService.sendPersonData.emit('tax');
              await this.signupService.updateUserStepPF(
                'register',
                'attachments',
                this.routeId,
              );
              this.activeStep++;
            }

            break;

          case 7:
            if (!this.hasRepresentatives) {
              this.router.navigate([`/app/dashboard`]);
            } else {
              this.activeStep++;

              const approvalDone = await this.sendApproval();
              if (approvalDone) {
                await this.signupService.updateUserStepPF(
                  'review',
                  'general',
                  this.routeId,
                );

                this.router.navigateByUrl(
                  `/app/details/${this.role}/${this.routeId}`,
                );
              }
            }

            break;

          case 8:
            this.router.navigate([`/app/dashboard`]);
            break;
        }
      } else {
        switch (this.activeStep) {
          case 0:
            let step = this.signupService.getPersonStep();

            if (step !== 'complementaryRepresentatives') {
              this.loading = false;
              this.signupService.sendPersonData.emit(step);
              return;
            }

            // Atualiza o step para attachments via register-step

            if (this.hasRepresentatives) {
              await this.signupService.updateUserStepPF(
                'register',
                'powers',
                this.routeId,
              );

              this.activeStep++;
            } else {
              // Se não tem representantes, vai direto para attachments (step 1)
              await this.signupService.updateUserStepPF(
                'register',
                'attachments',
                this.routeId,
              );
              this.activeStep = 1; // attachments é o step 1 quando não há representantes
            }

            break;

          case 1:
            if (!this.hasRepresentatives) {
              // Se não tem representantes, step 1 = attachments, então chama sendApproval diretamente
              this.activeStep++;

              const approvalDone = await this.sendApproval();
              if (approvalDone) {
                await this.signupService.updateUserStepPF(
                  'review',
                  'general',
                  this.routeId,
                );

                this.router.navigateByUrl(
                  `/app/details/${this.role}/${this.routeId}`,
                );
              }
            } else if (this.hasRepresentatives && this.isAssignor) {
              // Se tem representantes e é assignor, step 1 = powers, vai para attachments
              this.signupService.sendPersonData.emit('powers');
              await this.signupService.updateUserStepPF(
                'register',
                'attachments',
                this.routeId,
              );
              this.activeStep++;
            } else {
              // Se tem representantes mas não é assignor, step 1 = powers, vai para attachments
              this.activeStep++;
            }

            break;

          case 2:
            // Se tem representantes, step 2 = attachments, então chama sendApproval
            this.activeStep++;
            const approvalDone = await this.sendApproval();
            if (approvalDone) {
              await this.signupService.updateUserStepPF(
                'review',
                'general',
                this.routeId,
              );

              this.router.navigateByUrl(
                `/app/details/${this.role}/${this.routeId}`,
              );
            }

            break;

          case 3:
            this.router.navigate([`/app/dashboard`]);
            break;
        }
      }
    }

    this.loading = false;
  }

  setInviteInfo() {
    try {
      const user = this.authService.getUser();
      const invite = user.active_register.register.invite;

      if (!invite) return;

      this.label = this.getRoleLabel(this.role);

      this.name = this.name = invite.data.name
        ? invite.data.name.toUpperCase()
        : '';

      this.signupService.basicInfo = {
        name: invite.data.name,
        email: invite.data.email,
        document: invite.data.document,
      };
    } catch (error) {
      // Continua o fluxo mesmo se houver erro
    }
  }

  getDisabled() {
    const canProceed = this.canProceed();
    const disabled = !canProceed || this.loading;
    return disabled;
  }

  toggleSupportModal() {
    this.showSupportModal = !this.showSupportModal;
  }

  async sendRepresentativeEmailsTest() {
    if (!this.routeId) return;
    this.sendingRepresentativeEmails = true;
    try {
      await this.apiService.post({
        route: 'api/approvals/send-representative-emails/',
        params: { register_uuid: this.routeId },
        body: {},
        token: true,
      });
      this.toastService.show('info', 'Sucesso', 'API chamada com sucesso');
    } catch (error) {
      this.toastService.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao chamar API',
      );
    } finally {
      this.sendingRepresentativeEmails = false;
    }
  }

  async logoff() {
    // Limpar tudo de forma assíncrona e aguardar conclusão
    await this.authService.clearUser();

    // Verificar uma última vez se tudo foi limpo
    const userStillExists = localStorage.getItem('@app:user');
    const tokenStillExists = localStorage.getItem('@app:token');

    if (userStillExists || tokenStillExists) {
      // Se ainda existir, limpar manualmente
      if (userStillExists) localStorage.removeItem('@app:user');
      if (tokenStillExists) localStorage.removeItem('@app:token');

      // Aguardar um pouco mais para garantir
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    // Navegar com replaceUrl para evitar problemas de histórico
    this.router.navigateByUrl('/login', { replaceUrl: true });
  }

  async checkRepresentativesData() {
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      if (!this.routeId) {
        // Se não houver register_uuid, assume que não há representantes
        this.hasRepresentatives = false;
        this.changeSteps();
        return;
      }

      const { data } = await this.apiService.get<
        ApiResponse<RepresentativeResponse[]>
      >({
        route: 'api/registration/representatives-data/',
        params: { register_uuid: this.routeId },
        token: true,
      });

      this.hasRepresentatives = data.length > 0;
      this.changeSteps();
    } catch (error) {
      // Se houver erro (incluindo 404), assume que não há representantes
      if (error.status === 404) {
        this.hasRepresentatives = false;
      } else {
        // Para outros erros, também assume false para não bloquear o fluxo
        this.hasRepresentatives = false;
      }
      this.changeSteps();
    }
  }

  private async resetRepresentativesSignStatus(register_uuid: string) {
    if (!register_uuid) {
      return;
    }

    try {
      const response = await this.apiService.get<{
        data: Array<{
          id: number;
          signs_registration_form?: boolean;
          is_registration_form_mandatory?: boolean;
        }>;
      }>({
        route: 'api/registration/registration-sign-status/',
        params: { register_uuid },
        token: true,
      });

      const representatives = (response?.data || []).filter(
        (item) =>
          item?.signs_registration_form || item?.is_registration_form_mandatory,
      );
      if (representatives.length === 0) {
        return;
      }

      await Promise.all(
        representatives.map((item) =>
          this.apiService.patch({
            route: `api/registration/registration-sign-status/${item.id}/`,
            params: { register_uuid },
            body: { has_signed: false },
            token: true,
          }),
        ),
      );
    } catch (error) {
      // Ignorar erro para não bloquear o fluxo de envio
    }
  }

  changeSteps() {
    const powerStep = {
      icon: 'fas fa-file-alt',
      title: 'Resumo dos poderes',
      svg: 'assets/images/icon-inputdata-active.svg',
      slug: 'powers',
    };

    const powersIndex = this.steps.findIndex((step) => step.slug === 'powers');

    if (!this.hasRepresentatives) {
      // Remover o step "powers" se existir
      if (powersIndex !== -1) {
        this.steps.splice(powersIndex, 1);
      }
    } else {
      // Adicionar o step "powers" se não existir
      if (powersIndex === -1) {
        if (this.isShareholder) {
          // Para shareholders, powers deve ser inserido após "Dados complementares" (posição 1)
          // antes dos shareholderSteps (income, declarations, etc.)
          this.steps.splice(1, 0, powerStep);
        } else {
          // Para não-shareholders (cedente/prestador), powers deve ser inserido antes de attachments
          const attachmentsIndex = this.steps.findIndex(
            (step) => step.slug === 'attachments',
          );
          if (attachmentsIndex !== -1) {
            this.steps.splice(attachmentsIndex, 0, powerStep);
          } else {
            // Se não há step de attachments, adicionar no final
            this.steps.push(powerStep);
          }
        }
      }
    }
  }
}
